import React from 'react';
import {
  User, Mail, Building2, MapPin, Globe, Phone,
  Edit2,

  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth/data/context/AuthContext';
import { AdminProfile } from '../../auth/data/api/AuthClient';
import '../styles/ProfilePage.css';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { state: { user } } = useAuthContext();

  const display: Partial<AdminProfile> = user || {};
  const companyName = display.company_name || 'Admin Company';
  const initials = companyName.substring(0, 2).toUpperCase() || 'AD';

  return (
    <div className="pf-page">
      <main className="pf-main">
        <header className="pf-header">
          <div className="pf-header-info">
            <div className="pf-title-row">
              <button className="pf-back-btn" onClick={() => navigate(-1)}>
                <ArrowLeft size={24} />
              </button>
              <h1>Admin Profile</h1>
            </div>
            <p>Manage your company and contact information in one secure location.</p>
          </div>
          {user?.role_name === 'Super Admin' && (
            <div className="pf-actions">
              <button className="pf-btn pf-btn-edit" onClick={() => navigate('/dashboard/settings?tab=profile')}>
                <Edit2 size={16} /> Edit Profile
              </button>
            </div>
          )}
        </header>

        <div className="pf-content-grid">
          {/* ── Profile Details ── */}
          <div className="pf-details-area">
            {/* Top Profile Card */}
            <div className="pf-top-card">
              <div className="pf-avatar-wrapper">
                <div className="pf-avatar">
                  {initials}
                </div>
                <div className="pf-status-dot"></div>
              </div>
              <div className="pf-profile-info">
                <div className="pf-name-row">
                  <h2>{companyName}</h2>
                  <span className="pf-badge">Admin</span>
                </div>
                <div className="pf-contact-info">
                  <div className="pf-contact-item">
                    <Mail size={16} />
                    <span>{display.email_address || 'No email provided'}</span>
                  </div>
                  {display.contact_number && (
                    <div className="pf-contact-item">
                      <Phone size={16} />
                      <span>{display.contact_number}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pf-info-sections">
              {/* Company Information Section */}
              <div className="pf-section-card">
                <div className="pf-section-header">
                  <Building2 size={18} />
                  <h3>Company Information</h3>
                </div>
                <div className="pf-field-grid">
                  <Field label="Company Name" name="company_name" icon={<Building2 size={14} />} display={display} />
                  <Field label="Organization Type" name="organization_type" icon={<Building2 size={14} />} display={display} />
                  <Field label="Headquarters" name="headquarters_location" icon={<MapPin size={14} />} display={display} />
                  <Field label="Website URL" name="website_url" icon={<Globe size={14} />} display={display} />
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="pf-section-card">
                <div className="pf-section-header">
                  <User size={18} />
                  <h3>Contact Information</h3>
                </div>
                <div className="pf-field-grid">
                  <Field label="Account Email (Login)" name="email_address" icon={<Mail size={14} />} display={display} />
                  <Field label="Contact Email" name="contact_email_address" icon={<Mail size={14} />} display={display} />
                  <Field label="Contact Number" name="contact_number" icon={<Phone size={14} />} display={display} />
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default ProfilePage;

interface FieldProps {
  label: string;
  name: keyof AdminProfile;
  icon: React.ReactNode;
  display: Partial<AdminProfile>;
}

const Field: React.FC<FieldProps> = ({ label, name, icon, display }) => (
  <div className="pf-field-item">
    <label className="pf-field-label">
      {icon}
      {label}
    </label>
    <div className="pf-field-value">{(display[name] as string) || '—'}</div>
  </div>
);
